import { parseJSONValue } from "../json/parseJSONValue";
import React, { useState, useRef, useEffect } from "react";
import dangerIconRed from "../../assets/images/icons/dangerIconRed.svg";
import moment from "moment";
import { getDateFormat, getDateTimeFormat } from "./localServices";
import { hexToRgba } from "./color";
import AvatarName from "../../components/UniversalComponents/AvatarName";
import Tooltip from "@mui/material/Tooltip";
import { ReactComponent as EmailIcon } from "../../assets/images/emailCustomerCard.svg";
import { ReactComponent as PhoneIcon } from "../../assets/images/PhoneIconCustomerCard.svg";

export const getName = (value) => {
  return value ? value : "-";
};
export const getInitials = (value, reqLength = 2) => {
  return (
    value &&
    value
      ?.split(" ")
      .map((item) => item[0])
      .join("")
      .slice(0, reqLength)
  );
};

export const getPortalPosition = (ele, yPos, xPos, yShift = 0, xshift = 0) => {
  if (ele.current) {
    const rect = ele.current.getBoundingClientRect(); // Get the bounding rectangle
    const posY = rect[yPos];
    const posX = rect[xPos];
    return {
      top: posY + window.scrollY + yShift,
      left: posX + window.scrollX + xshift,
      borderRadius: "5",
    };
  }
};

export function getPath(input) {
  if (typeof input !== "string" || input.trim() === "") {
    throw new Error("Input must be a non-empty string.");
  }

  // Split the input into words, trim whitespace, and join with underscores
  const path = input
    .trim()
    .toLowerCase()
    .split(/\s+/) // Split by one or more spaces
    .join("_");

  return `/${path}`;
}

// Check if an object is in an array
export function isObjectInArray(array, targetObject, excludeKeys = []) {
  // return array.some((obj) => JSON.stringify(obj) === JSON.stringify(targetObject));
  return array?.some((obj) => areObjectsSimilar(obj, targetObject, excludeKeys));
}

// Check if an object is in an array
export function isObjectInArray2(array, targetObject, excludeKeys = []) {
  // return array.some((obj) => JSON.stringify(obj) === JSON.stringify(targetObject));
  return array.some((obj) => areObjectsSimilar2(obj, targetObject, excludeKeys));
}

export function areObjectsSimilar2(obj1, obj2, includeKeys = []) {
  if (obj1 === obj2) return true; // Same reference or primitive value
  if (typeof obj1 !== "object" || typeof obj2 !== "object" || obj1 == null || obj2 == null) {
    return false; // Not objects or one is null
  }

  const keysToCompare = includeKeys.length > 0 ? includeKeys : Object.keys(obj1);

  return keysToCompare.every((key) => {
    if (!(key in obj1) || !(key in obj2)) return false;
    return areObjectsSimilar2(obj1[key], obj2[key], includeKeys);
  });
}

// Add opacity to a hex color code
export function addOpacityToHex(hexColor, opacityLevel) {
  // Validate the hex color format
  if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hexColor)) {
    throw new Error("Invalid hex color code");
  }
  if (opacityLevel < 0 || opacityLevel > 9) {
    throw new Error("Opacity level must be between 0 and 9");
  }

  // Map opacity level (0 = 100%, 9 = 10%)
  const opacityMap = [
    "FF", // 0: 100%
    "E6", // 1: 90%
    "CC", // 2: 80%
    "B3", // 3: 70%
    "99", // 4: 60%
    "80", // 5: 50%
    "66", // 6: 40%
    "4D", // 7: 30%
    "33", // 8: 20%
    "1A", // 9: 10%
  ];
  const opacityHex = opacityMap[opacityLevel];

  // Normalize 3-digit hex to 6-digit hex
  if (hexColor.length === 4) {
    hexColor = `#${hexColor[1]}${hexColor[1]}${hexColor[2]}${hexColor[2]}${hexColor[3]}${hexColor[3]}`;
  }

  // Append the opacity hex to the color
  return `${hexColor}${opacityHex}`;
}

// Check if two objects are similar
// export function areObjectsSimilar(obj1, obj2) {
//   if (obj1 === obj2) return true; // Same reference or primitive value
//   if (typeof obj1 !== "object" || typeof obj2 !== "object" || obj1 == null || obj2 == null) {
//     return false; // If not objects or either is null
//   }

//   const keys1 = Object.keys(obj1);
//   const keys2 = Object.keys(obj2);

//   if (keys1.length !== keys2.length) return false; // Different number of keys

//   // Compare all keys and values recursively
//   return keys1.every((key) => areObjectsSimilar(obj1[key], obj2[key]));
// }

export function areObjectsSimilar(obj1, obj2, excludeKeys = []) {
  if (obj1 === obj2) return true; // Same reference or primitive value
  if (typeof obj1 !== "object" || typeof obj2 !== "object" || obj1 == null || obj2 == null) {
    return false; // If not objects or either is null
  }

  const keys1 = Object.keys(obj1).filter((key) => !excludeKeys.includes(key));
  const keys2 = Object.keys(obj2).filter((key) => !excludeKeys.includes(key));

  if (keys1.length !== keys2.length) return false; // Different number of keys after filtering

  // Compare all keys and values recursively
  return keys1.every((key) => areObjectsSimilar(obj1[key], obj2[key], excludeKeys));
}

export function getThinScrollBar() {
  return `max-h-[400px]
  [&::-webkit-scrollbar]:w-2
  [&::-webkit-scrollbar-track]:bg-gray-100
  [&::-webkit-scrollbar-thumb]:bg-gray-300
  dark:[&::-webkit-scrollbar-track]:bg-neutral-700
  dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500`;
}
export function getThinScrollBarRounded() {
  return `max-h-[400px]
    [&::-webkit-scrollbar]:w-2
    [&::-webkit-scrollbar-track]:rounded-full
    [&::-webkit-scrollbar-track]:bg-gray-100
    [&::-webkit-scrollbar-thumb]:rounded-full
    [&::-webkit-scrollbar-thumb]:bg-gray-300
    dark:[&::-webkit-scrollbar-track]:bg-neutral-700
    dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500`;
}

export function getTitleCase(str) {
  if (typeof str !== "string" || str.length === 0) return "";
  return str
    .split(/[_ ]+/) // Split by one or more underscores or spaces
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function getSentenceCase(str) {
  if (typeof str !== "string" || str.length === 0) return "";

  // special case
  if (str.toLowerCase() === "project") {
    return "Jobs";
  }

  return str
    ?.split("_")
    .map((word, i) => {
      if (i === 0) return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      else return word.toLowerCase();
    })
    .join(" ");
}

export function transformAndReorder(objList, keysToMove = []) {
  try {
    if (!Array.isArray(objList)) {
      throw new Error("Input must be an array of objects.");
    }

    return objList.map((obj) => {
      if (typeof obj !== "object" || obj === null) {
        throw new Error("Each item in the array must be a non-null object.");
      }

      let newObj = {};

      // Process keys that are not in keysToMove
      Object.keys(obj).forEach((key) => {
        if (!keysToMove.includes(key)) {
          newObj[key] =
            typeof obj[key] === "object" && obj[key] !== null ? JSON.stringify(obj[key]) : parseJSONValue(obj[key]);
        }
      });

      // Append keysToMove at the end in the given order
      keysToMove.forEach((key) => {
        if (obj.hasOwnProperty(key)) {
          newObj[key] =
            typeof obj[key] === "object" && obj[key] !== null ? JSON.stringify(obj[key]) : parseJSONValue(obj[key]);
        }
      });

      return newObj;
    });
  } catch (error) {
    console.error("An error occurred while processing the object list:", error);
    return []; // Return an empty array in case of an error
  }
}

export function getFormData(dataObject) {
  const formData = new FormData();
  // Check if dataObject is an array
  Object.keys(dataObject).forEach((key) => {
    const value = dataObject[key];

    // Handle attachments (assuming it's an array of file objects)
    if (key === "attachment" && Array.isArray(value)) {
      value.forEach((fileObj, index) => {
        if (fileObj?.file) {
          formData.append(`attachment[${index}]`, fileObj.file);
        }
      });
    } else if (Array.isArray(value)) {
      // Convert array to JSON string and append it
      formData.append(key, JSON.stringify(value));
    } else if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });

  return formData;
}

export function parseJsonValues(obj) {
  if (typeof obj !== "object" || obj === null) return obj;

  return Object.keys(obj).reduce((acc, key) => {
    let value = obj[key];
    if (typeof value === "string") {
      try {
        // Attempt to parse the string
        const parsed = JSON.parse(value);
        // If the parsed value is an object or array, process it recursively
        value = typeof parsed === "object" && parsed !== null ? parseJsonValues(parsed) : parsed;
      } catch (error) {
        // If JSON.parse fails, leave value as is
      }
    } else if (typeof value === "object") {
      // Recursively handle nested objects
      value = parseJsonValues(value);
    }
    acc[key] = value;
    return acc;
  }, {});
}

export const flattenTree = (nodes, childrenKey = "children") => {
  const result = [];
  const traverse = (node) => {
    // Destructure children and keep the rest of the properties
    const { [childrenKey]: children, ...rest } = node;
    result.push(rest);
    // If children exists and is an array, recursively process them
    if (children && Array.isArray(children)) {
      children?.forEach((child) => traverse(child));
    }
  };
  nodes?.forEach((node) => traverse(node));
  return result;
};

export const getPersonInitials = (person) => {
  const nameParts = person?.name?.split(" ");
  // const initials = nameParts?.map((part) => part[0]?.toUpperCase()).join("");
  const initials = nameParts[0][0]?.toUpperCase() + nameParts[nameParts.length - 1][0]?.toUpperCase();
  const background = person?.c_key || "#ccc";
  return { initials, background };
};

export function safeJsonParse(value) {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (e) {
      // Return the original string if it fails to parse
      return value;
    }
  }
  return value;
}

export function safeJsonStringify(value) {
  if (typeof value === "string") {
    return value; // already a string, return as-is
  }
  try {
    return JSON.stringify(value);
  } catch (e) {
    // Return original value if it fails (could be circular reference, etc.)
    return value;
  }
}

export function parseAndExtract(input, condition, notJsx = false) {
  try {
    const parsed = safeJsonParse(input);
    const value = parsed?.[condition] ?? input;

    if (notJsx) {
      return value;
    }

    const link = parsed?.url;
    if (link) {
      const hasProtocol = /^https?:\/\//i.test(link);
      const finalHref = hasProtocol ? link : `https://${link}`;

      return (
        <a
          href={finalHref}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </a>
      );
    }

    return value;
  } catch {
    return input;
  }
}

export function capitalizeString(str) {
  if (typeof str !== "string") return str;
  return str
    .toLowerCase()
    .replace(/_/g, " ") // optional: replace underscores with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export const getColorForName = (name) => {
  const parts = name?.trim()?.split(" ");
  const first = parts[0]?.charAt(0).toUpperCase() || "";
  const middle = parts.length > 2 ? parts[Math.floor(parts.length / 2)].charAt(0).toUpperCase() : "";
  const last = parts[parts.length - 1]?.charAt(0).toUpperCase() || "";
  const charCodeSum = (first.charCodeAt(0) || 0) + (middle.charCodeAt(0) || 0) + (last.charCodeAt(0) || 0);

  return predefinedColors[charCodeSum % predefinedColors.length];
};
// export function capitalizeString(str) {
//   if (typeof str !== "string") return str;
//   return str
//     .toLowerCase()
//     .replace(/_/g, " ") // optional: replace underscores with spaces
//     .replace(/\b\w/g, (char) => char.toUpperCase());
// }

export const getTextAlignment = (type) => {
  switch (type) {
    case "text":
      return "left";
    case "number":
      return "right";
    case "date":
      return "center";
    case "time":
      return "center";
    default:
      return "left";
  }
};

export function formatNegativeNumber(value, format, selectedOption, decimalPlaces, useSeparator, symbol) {
  const optionListCurrency = [
    { label: "₹", value: "INR" },
    { label: "$", value: "USD" },
    { label: "€", value: "EUR" },
    { label: "£", value: "GBP" },
    { label: "¥", value: "JPY" },
  ];

  const absValue = Math.abs(value);
  const isNegative = value < 0;

  const getFormattedValue = (num) => {
    let fomattedNum = num;

    if (String(decimalPlaces)) fomattedNum = Number(fomattedNum).toFixed(decimalPlaces);

    if (Boolean(useSeparator)) {
      fomattedNum = addThousandSeparator(fomattedNum, decimalPlaces);
    }

    if (selectedOption.toLowerCase() === "currency" && symbol) {
      fomattedNum = `${optionListCurrency?.find((item) => item.label === symbol)?.label || ""} ${fomattedNum}`;
    }

    if (selectedOption.toLowerCase() === "percentage") {
      fomattedNum = `${fomattedNum} %`;
    }

    return fomattedNum;
  };

  if (value === undefined || value === null) return null;
  if (value >= 0) return <span>{getFormattedValue(value)}</span>;
  // if (typeof value !== "number") {
  //   throw new Error("Value must be a number");
  // }

  function addThousandSeparator(numStr, decimalPlaces) {
    if (numStr === undefined || numStr === null || numStr === "") return null;
    if (typeof numStr === "number") numStr = numStr.toString();

    const [integerPart, decimalPart] = numStr ? numStr?.split(".") : ["", ""];

    const formattedInteger = integerPart?.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return `${formattedInteger}${decimalPlaces ? "." : ""}${decimalPart ? decimalPart.slice(0, decimalPlaces) : "0".repeat(decimalPlaces)}`;
  }

  switch (format) {
    case "minus":
      return (
        <span>
          {isNegative
            ? selectedOption.toLowerCase() === "percentage"
              ? getFormattedValue(-absValue)
              : `-${getFormattedValue(absValue)}`
            : getFormattedValue(absValue)}
        </span>
      );

    case "red-minus":
      return (
        <span>
          {isNegative
            ? selectedOption.toLowerCase() === "percentage"
              ? getFormattedValue(-absValue)
              : `-${getFormattedValue(absValue)}`
            : getFormattedValue(absValue)}
        </span>
      );

    case "parentheses":
      return <span>{isNegative ? `(${getFormattedValue(absValue)})` : getFormattedValue(absValue)}</span>;

    case "red-parentheses":
      return (
        <span style={{ color: "red" }}>
          {isNegative ? `(${getFormattedValue(absValue)})` : getFormattedValue(absValue)}
        </span>
      );

    case "red":
      return <span style={{ color: "red" }}>{getFormattedValue(absValue)}</span>;

    default:
      return (
        <span>
          {isNegative
            ? selectedOption.toLowerCase() === "percentage"
              ? getFormattedValue(-absValue)
              : `-${getFormattedValue(absValue)}`
            : getFormattedValue(absValue)}
        </span>
      );
  }
}

export function formatTextValue(value, formatType, isMySpace = false) {
  let trimmedValue;
  if (isMySpace) {
    if (value?.value === undefined || value?.value === null) return null;
    trimmedValue = value?.value;
  } else {
    if (value === undefined || value === null) return null;
    trimmedValue = String(value).trim();
  }

  switch (formatType) {
    case "Email": {
      const email = trimmedValue.toLowerCase();
      const emailParts = email.split("@");
      if (
        emailParts.length !== 2 ||
        email.startsWith("@") ||
        email.endsWith("@") ||
        email.startsWith(".") ||
        email.endsWith(".")
      ) {
        return <span className="text-red-500">Invalid email format</span>;
      }

      const [local, domain] = emailParts;

      const localPartRegex = /^[a-zA-Z0-9._+-]+$/;
      if (!localPartRegex.test(local)) {
        return <span className="text-red-500">Invalid characters in email</span>;
      }

      const domainParts = domain.split(".");
      const validTLDs = ["com", "org", "net", "in", "co", "edu", "gov", "info", "biz", "io", "dev"];

      // Must have exactly 2 domain parts: domain + tld
      if (
        domainParts.length !== 2 ||
        !local ||
        !domainParts[0] || // domain name before TLD
        !validTLDs.includes(domainParts[1]) // valid TLD
      ) {
        return <span className="text-red-500">Invalid email domain</span>;
      }

      return (
        <a href={`mailto:${email}`} className="text-ellipsis truncate">
          {email}
        </a>
      );
    }
    case "Sort Text": {
      if (!trimmedValue) {
        return <span className="text-red-500">Empty text</span>;
      }

      const isTruncated = trimmedValue.length > 20;
      const displayText = isTruncated ? `${trimmedValue.slice(0, 20)}...` : trimmedValue;

      return <span title={trimmedValue}>{displayText}</span>;
    }

    case "Long Text": {
      if (!trimmedValue) {
        return <span className="text-red-500">Empty text</span>;
      }

      const isTruncated = trimmedValue.length > 150;
      const displayText = isTruncated ? `${trimmedValue.slice(0, 150)}...` : trimmedValue;

      return (
        <div className="text-ellipsis truncate w-full" title={trimmedValue}>
          {displayText}
        </div>
      );
    }

    case "URL":
      const trimmed = trimmedValue.trim();

      if (!trimmed) {
        return <span className="text-red-500">URL cannot be empty</span>;
      }

      const hasProtocol = /^https?:\/\//i.test(trimmed);
      const formattedUrl = hasProtocol ? trimmed : `https://${trimmed}`;

      try {
        const url = new URL(formattedUrl);
        const hostname = url.hostname;

        // Disallow localhost and pure IP addresses
        if (hostname === "localhost" || /^[\d.]+$/.test(hostname)) {
          return <span className="text-red-500">Invalid URL format</span>;
        }

        // Validate TLD
        const validTLDs = [
          "com",
          "org",
          "net",
          "in",
          "co",
          "edu",
          "gov",
          "info",
          "biz",
          "io",
          "dev",
          "website",
          "inc",
          "tech",
          "app",
          "store",
          "online",
          "site",
          "ai",
          "me",
          "us",
          "tv",
          "cc",
          "pro",
          "blog",
          "shop",
        ];
        const domainParts = hostname.split(".");
        const tld = domainParts[domainParts.length - 1];

        if (!validTLDs.includes(tld)) {
          return <span className="text-red-500">Invalid URL format</span>;
        }

        // Disallow weird subdomains like "wwwwww"
        const subdomain = domainParts.length > 2 ? domainParts[0] : null;
        if (subdomain && /^w{4,}$/.test(subdomain)) {
          return <span className="text-red-500">Invalid URL format</span>;
        }

        // Validate overall domain format
        const domainRegex = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
        if (!domainRegex.test(hostname)) {
          return <span className="text-red-500">Invalid URL format</span>;
        }

        return (
          <a
            href={url.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
            onClick={(e) => e.stopPropagation()}
          >
            {url.href}
          </a>
        );
      } catch {
        return <span className="text-red-500">Invalid URL format</span>;
      }

    // case "Hyperlink": {
    //   return <>{parseAndExtract(value, "textForUrl")}</>;
    // }
    case "Hyperlink": {
      let parsed;
      try {
        if (isMySpace) {
          parsed = safeJsonParse(value?.value);
        } else {
          parsed = safeJsonParse(value);
        }
      } catch {
        return <span className="text-red-500">Invalid input format</span>;
      }

      const link = parsed?.url?.trim();
      const displayText = parsed?.textForUrl?.trim() || parsed?.url?.trim() || "Link";

      if (!link) {
        return <span className="text-red-500">URL cannot be empty</span>;
      }

      const hasProtocol = /^https?:\/\//i.test(link);
      const finalHref = hasProtocol ? link : `https://${link}`;

      try {
        const parsedUrl = new URL(finalHref);
        const domain = parsedUrl.hostname;

        // Disallow localhost and IP addresses
        if (domain === "localhost" || /^[\d.]+$/.test(domain)) {
          return <span className="text-red-500">Invalid URL</span>;
        }

        // Validate TLD
        const allowedTLDs = [
          "com",
          "org",
          "net",
          "in",
          "co",
          "edu",
          "gov",
          "info",
          "biz",
          "io",
          "dev",
          "website",
          "inc",
          "tech",
          "app",
          "store",
          "online",
          "site",
          "ai",
          "me",
          "us",
          "tv",
          "cc",
          "pro",
          "blog",
          "shop",
        ];
        const domainParts = domain.split(".");
        const tld = domainParts[domainParts.length - 1];
        if (!allowedTLDs.includes(tld)) {
          return <span className="text-red-500">Invalid URL</span>;
        }

        // Disallow suspicious subdomains like "wwwwww"
        const subdomain = domainParts.length > 2 ? domainParts[0] : null;
        if (subdomain && /^w{4,}$/.test(subdomain)) {
          return <span className="text-red-500">Invalid URL</span>;
        }

        // Domain format check
        const domainPattern = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
        if (!domainPattern.test(domain)) {
          return <span className="text-red-500">Invalid URL</span>;
        }

        return (
          <a
            href={parsedUrl.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
            onClick={(e) => e.stopPropagation()}
          >
            {displayText}
          </a>
        );
      } catch {
        return <span className="text-red-500">Invalid URL format</span>;
      }
    }

    default:
      if (!trimmedValue) {
        return <span className="text-primary-text-muted">No content</span>;
      }
      return <span>{trimmedValue}</span>;
  }
}

export const getUserDate = (dateString) => {
  const date = moment(dateString, [
    "YYYY-MM-DD",
    "D MMM YYYY",
    "DD-MM-YYYY",
    "MMM D, YYYY",
    "MM/DD/YYYY",
    "DD/MM/YYYY",
    "DD-MMMM-YYYY",
    "YYYY-MMM-DD",
    "DD.MM.YYYY",
  ]);
  return date.isValid() ? date.format(getDateFormat()) : "-";
};

export const getFormatedDate = (dateFormate, dateString = null) => {
  // const dateStringRaw = dateString ?? "2025-12-31";
  const date = dateString
    ? moment(dateString, ["YYYY-MM-DD", "D MMM YYYY", "DD-MM-YYYY", "MMM D, YYYY", "MM/DD/YYYY", "DD/MM/YYYY"])
    : moment(); // dynamic current date
  return date.isValid() ? date.format(dateFormate) : "-";
};

export const getUserDateTime2 = (dateString) => {
  if (!dateString || dateString.trim() === "") {
    return " ";
  }

  // Define input formats for date-only, time-only, and date+time
  const inputFormats = [
    // Date + Time formats
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD HH:mm:ss",
    "DD/MM/YYYY HH:mm",
    "DD/MM/YYYY HH:mm:ss",
    "MM/DD/YYYY HH:mm",
    "MM/DD/YYYY HH:mm:ss",
    "DD-MM-YYYY HH:mm",
    "DD-MM-YYYY HH:mm:ss",
    "D MMM YYYY HH:mm",
    "D MMM YYYY HH:mm:ss",
    "Do MMMM YYYY hh:mm A",
    "Do MMMM YYYY HH:mm",
    "MMM D, YYYY HH:mm",
    "MMM D, YYYY HH:mm:ss",
    "dddd, MMMM Do YYYY HH:mm",
    "dddd, MMMM Do YYYY hh:mm A",
    // Date-only formats
    "YYYY-MM-DD",
    "DD/MM/YYYY",
    "MM/DD/YYYY",
    "DD-MM-YYYY",
    "DD.MM.YYYY",
    "D MMM YYYY",
    "Do MMM YYYY",
    "Do MMMM YYYY",
    "YYYY-MMM-DD",
    "MMMM Do YYYY",
    "dddd, MMMM Do YYYY",
    // Time-only formats
    "HH:mm",
    "HH:mm:ss",
    "hh:mm A",
    "hh:mm:ss A",
    "h:mm A",
    "h:mm:ss A",
  ];

  // Parse the dateString with strict mode
  const date = moment(dateString.trim(), inputFormats, true);
  if (!date.isValid()) {
    console.warn("Invalid date string:", dateString, "Parsed formats:", inputFormats);
    return "-";
  }

  // Detect input type
  const isTimeOnlyInput =
    !dateString.includes("-") &&
    !dateString.includes("/") &&
    !dateString.includes(" ") &&
    (dateString.includes(":") || dateString.match(/^[0-9]{1,2}:[0-9]{2}(?:\s?[AP]M)?$/i));
  const isDateOnlyInput =
    dateString.match(/^\d{4}-\d{2}-\d{2}$/) ||
    dateString.match(/^\d{2}\/\d{2}\/\d{4}$/) ||
    dateString.match(/^\d{2}-\d{2}-\d{4}$/) ||
    dateString.match(/^\d{1,2}\s[A-Za-z]{3}\s\d{4}$/) ||
    dateString.match(/^\d{1,2}(?:st|nd|rd|th)?\s[A-Za-z]+\s\d{4}$/);

  // Get formats from localStorage
  const { dateFormate, timeFormate } = getDateTimeFormat();

  // Defaults
  let finalDateFormat = dateFormate || "";
  let finalTimeFormat = timeFormate || "";

  // Handle formatting based on input type
  let formattedOutput = "";
  if (isTimeOnlyInput) {
    // Time-only input: format only time, ignore dateFormate
    if (finalTimeFormat) {
      formattedOutput = date.format(finalTimeFormat);
    } else {
      formattedOutput = date.format("HH:mm");
    }
  } else if (isDateOnlyInput) {
    // Date-only input: format only date, ignore timeFormate
    if (finalDateFormat) {
      formattedOutput = date.format(finalDateFormat);
    } else {
      formattedOutput = date.format("DD MMM YYYY");
    }
  } else {
    // Date+time input: format both, but respect empty formats
    if (finalDateFormat && finalTimeFormat) {
      formattedOutput = date.format(`${finalDateFormat} ${finalTimeFormat}`);
    } else if (finalDateFormat) {
      formattedOutput = date.format(finalDateFormat);
    } else if (finalTimeFormat) {
      formattedOutput = date.format(finalTimeFormat);
    } else {
      formattedOutput = date.format("DD MMM YYYY HH:mm");
    }
  }

  return formattedOutput.trim();
};

/**
 * Extracts a unique list of keys from an array of objects.
 *
 * @param {Array} arrayOfObjects - The input array containing objects.
 * @returns {Array} - An array of unique keys found across all objects.
 *
 * Edge cases handled:
 * - Input is not an array
 * - Objects containing null or undefined
 * - Non-object entries (e.g., numbers, strings, arrays)
 * - Duplicate keys across objects
 */
export function extractKeysFromObjects(arrayOfObjects) {
  // Check if input is a valid array
  if (!Array.isArray(arrayOfObjects)) {
    throw new TypeError("Input must be an array");
  }

  // Use a Set to store keys uniquely
  const keySet = new Set();

  // Iterate over each item in the array
  arrayOfObjects.forEach((item) => {
    // Ensure the item is a non-null object and not an array
    if (item && typeof item === "object" && !Array.isArray(item)) {
      // Get the object's own enumerable keys and add them to the Set
      Object.keys(item).forEach((key) => keySet.add(key));
    }
    // Non-object values are ignored
  });

  // Convert the Set to an array and return it
  return Array.from(keySet);
}

/**
 * Detects the type of each column as either 'string' or 'number'.
 *
 * @param {Array<Object>} data - Array of objects parsed from CSV.
 * @returns {Object} - An object where keys are column names and values are 'string' or 'number'.
 *
 * Notes:
 * - A column is 'number' only if all non-empty values are numeric.
 * - Otherwise, the column is considered 'string'.
 */
export function detectColumnTypes(data) {
  if (!Array.isArray(data)) {
    throw new TypeError("Input must be an array of objects");
  }

  const columnTypes = {};

  data.forEach((row) => {
    if (row && typeof row === "object" && !Array.isArray(row)) {
      Object.entries(row).forEach(([key, value]) => {
        // Skip undefined, null, or empty strings
        if (value === undefined || value === null || value === "") return;

        // Initialize column type as 'number' by default
        if (!columnTypes[key]) {
          columnTypes[key] = "number";
        }

        // If value is not a valid number, mark the column as 'string'
        if (isNaN(value)) {
          columnTypes[key] = "string";
        }
      });
    }
  });

  return columnTypes;
}

export function truncateString({ str, maxLength = 20 }) {
  return str?.length > maxLength ? str?.slice(0, maxLength) + "..." : str;
}

export function getFileExtension(filename) {
  return filename?.split(".")?.pop()?.toLowerCase();
}

export function removeFileExtension(filename) {
  if (typeof filename !== "string") {
    throw new TypeError("Expected a string as filename");
  }

  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) return filename; // No extension
  if (lastDotIndex === 0) return filename; // Hidden file like ".gitignore"
  return filename.substring(0, lastDotIndex);
}

export function IndeterminateCheckbox({
  indeterminate,
  className = "switch border-gray-300 hover:border-[var(--color-primary)] checked:bg-[var(--color-primary)] checked:border-[var(--color-primary)] rounded-sm mx-3 appearance-none border outline-none focus:outline-none focus:ring-0 cursor-pointer",
  ...rest
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof indeterminate === "boolean" && ref.current) {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [indeterminate, rest.checked]);

  return <input type="checkbox" ref={ref} className={`${className} cursor-pointer`} {...rest} indeterminate />;
}

export function getHexWithOpacity(hex = "#000000", opacityPercent = 100, background = "#FFFFFF") {
  // Remove #
  hex = hex?.replace("#", "");
  background = background?.replace("#", "");

  // Parse hex to RGB
  const fgR = parseInt(hex?.substring(0, 2), 16);
  const fgG = parseInt(hex?.substring(2, 4), 16);
  const fgB = parseInt(hex?.substring(4, 6), 16);

  const bgR = parseInt(background?.substring(0, 2), 16);
  const bgG = parseInt(background?.substring(2, 4), 16);
  const bgB = parseInt(background?.substring(4, 6), 16);

  const alpha = Math.max(0, Math.min(1, opacityPercent / 100));

  // Blend foreground with background
  const r = Math.round(fgR * alpha + bgR * (1 - alpha));
  const g = Math.round(fgG * alpha + bgG * (1 - alpha));
  const b = Math.round(fgB * alpha + bgB * (1 - alpha));

  // Convert to hex
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
}

export function getUniqueTags(data) {
  const tagMap = new Map();

  if (!data || !Array.isArray(data.notes)) return [];

  data.notes.forEach((note) => {
    if (Array.isArray(note.tags)) {
      note.tags.forEach((tag) => {
        if (tag && tag.label && !tagMap.has(tag.label)) {
          tagMap.set(tag.label, tag);
        }
      });
    }
  });

  return Array.from(tagMap.values());
}
export function filterNotesByTags(data, selectedTags) {
  console.log("selectedTagsss", data, selectedTags);

  if (!data || !Array.isArray(data.notes)) return data;

  // If no selectedTags or it's empty, return original data as-is
  if (!Array.isArray(selectedTags) || selectedTags.length === 0) {
    return data;
  }

  const selectedValues = selectedTags.map((tag) => tag.label);

  console.log("selectedTagsss Values", selectedValues);

  const filteredNotes = data.notes.filter(
    (note) => Array.isArray(note.tags) && note.tags.some((tag) => selectedValues.includes(tag.label))
  );

  return {
    ...data,
    notes: filteredNotes,
    total: filteredNotes.length,
  };
}

export const predefinedColors = [
  "#782AEE",
  "#DB4A6B",
  "#7A518E",
  "#C46D3A",
  "#E38BAB",
  "#5F5E52",
  "#813B3B",
  "#99031C",
  "#005D85",
  "#731B5B",
  "#DF2982",
  "#949E26",
  "#817D02",
  "#310082",
  "#9A0D94",
  "#266B89",
  "#ED592E",
  "#3DB22A",
  "#2F18DB",
  "#164A79",
  "#9B6F00",
  "#007E9A",
  "#EF4444",
  "#134E30",
  "#8C7A75",
  "#65657F",
  "#80703F",
  "#D47A46",
  "#248B5B",
  "#26364C",
  "#D9E000",
  "#0E8BFE",
];

export const TagOptionsColored = [
  { value: "Follow Up", label: "Follow Up", color_code: "#782AEE" },
  { value: "Pending Approval", label: "Pending Approval", color_code: "#DB4A6B" },
  { value: "To Do", label: "To Do", color_code: "#7A518E" },
  { value: "Scheduled", label: "Scheduled", color_code: "#C46D3A" },
  { value: "Recurring TaskRisk", label: "Recurring TaskRisk", color_code: "#E38BAB" },
  { value: "Escalation", label: "Escalation", color_code: "#5F5E52" },
  { value: "Urgent", label: "Urgent", color_code: "#813B3B" },
  { value: "Compliance", label: "Compliance", color_code: "#99031C" },
  { value: "Discrepancy", label: "Discrepancy", color_code: "#005D85" },
  { value: "Client Feedback", label: "Client Feedback", color_code: "#731B5B" },
  { value: "Meeting Summary", label: "Meeting Summary", color_code: "#DF2982" },
  { value: "Onboarding", label: "Onboarding", color_code: "#949E26" },
  { value: "Lead Qualified", label: "Lead Qualified", color_code: "#817D02" },
  { value: "Churn Risk", label: "Churn Risk", color_code: "#310082" },
  { value: "Invoice Issue", label: "Invoice Issue", color_code: "#9A0D94" },
  { value: "Tax Notes", label: "Tax Notes", color_code: "#266B89" },
  { value: "Payment Reminder", label: "Payment Reminder", color_code: "#ED592E" },
  { value: "Audit Trail", label: "Audit Trail", color_code: "#3DB22A" },
  { value: "Clarification Needed", label: "Clarification Needed", color_code: "#2F18DB" },
  { value: "Review Required", label: "Review Required", color_code: "#164A79" },
  { value: "Data Verified", label: "Data Verified", color_code: "#9B6F00" },
  { value: "Policy Update", label: "Policy Update", color_code: "#007E9A" },
  { value: "Reminder", label: "Reminder", color_code: "#EF4444" },
  { value: "Blocked", label: "Blocked", color_code: "#134E30" },
  { value: "Client Escalation", label: "Client Escalation", color_code: "#8C7A75" },
  { value: "Overdue", label: "Overdue", color_code: "#65657F" },
  { value: "Waiting Feedback", label: "Waiting Feedback", color_code: "#80703F" },
  { value: "Manager Review", label: "Manager Review", color_code: "#D47A46" },
  { value: "In Discussion", label: "In Discussion", color_code: "#248B5B" },
  { value: "Submitted", label: "Submitted", color_code: "#26364C" },
  { value: "Contract Review", label: "Contract Review", color_code: "#D9E000" },
  { value: "Shared With Client", label: "Shared With Client", color_code: "#0E8BFE" },
];

export const convertBytesToReadableSize = ({ bytes = 0, decimals = 2, unit = "auto" }) => {
  //units Can be: 'auto', 'Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'

  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];

  let index;

  if (unit === "auto") {
    index = Math.floor(Math.log(bytes) / Math.log(k));
  } else {
    index = sizes.indexOf(unit);
    if (index === -1) {
      throw new Error(`Invalid unit "${unit}". Valid options: ${sizes.join(", ")}`);
    }
  }

  const value = parseFloat((bytes / Math.pow(k, index)).toFixed(decimals));
  return `${value} ${sizes[index]}`;
};

export const getTextWithColor = (parsed) => {
  const color = parsed?.color;
  if (!parsed?.value) return "--";
  return (
    <div
      style={{
        backgroundColor: hexToRgba(color, 0.1),
        color: color ? hexToRgba(color) : "black",
        border: color ? `0.8px solid ${color}` : "none",
        borderRadius: "5px",
        whiteSpace: "nowrap",
      }}
      className="inline-block text-[12px] px-2 min-h-[20px] text-ellipsis"
    >
      {parsed.value}
    </div>
  );
};

export const getMultiTagString = (value) => {
  try {
    const parsed = safeJsonParse(value);
    if (Array.isArray(parsed)) {
      const maxVisible = 3;
      const visibleItems = parsed.slice(0, maxVisible);
      const remainingCount = parsed.length - maxVisible;

      return (
        <p className="px-2 py-1 rounded w-fit flex flex-wrap gap-1 max-w-full">
          {visibleItems.map((item, i) => {
            const color = item?.color_code || item?.code;
            return (
              <span
                key={i}
                style={{
                  backgroundColor: hexToRgba(color, 0.1),
                  color: color ? hexToRgba(color) : "black",
                  border: color ? `0.8px solid ${color}` : "none",
                  borderRadius: "5px",
                  whiteSpace: "nowrap",
                }}
                className="px-2 py-0.5 text-[12px] border rounded whitespace-nowrap"
              >
                {item.value || "--"}
              </span>
            );
          })}
          {remainingCount > 0 && (
            <span className="px-2 py-0.5 text-[12px] border rounded bg-[#607791] text-white whitespace-nowrap">
              +{remainingCount}
            </span>
          )}
        </p>
      );
    }

    if (typeof parsed === "object" && parsed?.value) {
      const bg = parsed?.color_code ? `${parsed.color_code}99` : undefined;
      return (
        <p
          className="px-2 py-1 rounded w-fit max-w-[10vw] text-ellipsis overflow-hidden"
          style={{ backgroundColor: bg, color: bg ? "white" : "black" }}
        >
          {parsed.value}
        </p>
      );
    }
  } catch {
    return <p>{value}</p>;
  }
};

export const getValueDiv = (value) => {
  if (typeof value === "object" && value !== null) return value.value;
  return <div className="min-h-[20px] py-2 w-full text-ellipsis">{value}</div>;
};

export const getPersonAvatars = (list) => {
  if (!Array.isArray(list)) return null;
  const visible = list.slice(0, 3);

  return (
    <div className="w-full pt-1 flex items-end space-x-1">
      {visible.map((p, i) => {
        const initials = p.name
          .split(" ")
          .filter(Boolean)
          .map((w) => w.charAt(0).toUpperCase())
          .slice(0, 2)
          .join("");

        return (
          <Tooltip
            key={i}
            placement="top"
            arrow
            title={
              <div className="py-2 min-w-[249px] rounded-lg bg-white border-b-2 pb-2 space-y-2">
                <div className="flex px-2 space-x-2">
                  <AvatarName
                    name={initials}
                    size="small"
                    bgColor={"#E8ECF299"}
                    textColor={"#7C7C83"}
                    borderColor={"#ABABAC"}
                  />
                  <div className="text-2999BC text-14">{p.name}</div>
                </div>
                <div className="flex px-2 space-x-2 my-2">
                  <div className="w-5 text-center">
                    <EmailIcon />
                  </div>
                  <div className="text-F16136 text-12">{p.email}</div>
                </div>
                <div className="flex px-2 space-x-2 my-2">
                  <div className="w-5 text-center">
                    <PhoneIcon />
                  </div>
                  <div className="text-F16136 text-12">{p.mobile || "Not Available"}</div>
                </div>
              </div>
            }
          >
            <div
              className={`${
                p?.c_key ? "text-white border-white" : "text-[#7C7C83] border-[#ABABAC]"
              } text-12 font-400 p-[10px] w-7 h-7 flex items-center justify-center rounded-full border`}
              style={{ backgroundColor: p?.c_key || "#E8ECF2" }}
            >
              {initials}
            </div>
          </Tooltip>
        );
      })}
      {list.length > 3 && (
        <div className="bg-gray-200 text-gray-500 text-12 font-600 border-gray-300 w-7 h-7 flex items-center justify-center rounded-full border">
          +{list.length - 3}
        </div>
      )}
    </div>
  );
};
